import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import Masonry from 'react-masonry-css';

import QuestionCard from '../../QuestionCard/QuestionCard';
import SkeletonCard from '../../QuestionCard/SkeletonCard';
import { BaseButton, useAuth, useLoading, useT } from '@repo/common';

import { ReactComponent as ArrowIcon } from '@repo/common/assets/icons/20px/icon_20px_arrow-bottom.svg';
import { ReactComponent as ResetIcon } from '@repo/common/assets/icons/20px/icon_20px_reset.svg';

import { getQuestionList, getUserLikeList } from '../../../api/qna/qna.api';

const QnaList = ({
  activeCategory,
  isCategoryChangeRef,
  filterState,
  useIn,
  searchResult,
  searchKeyword,
}) => {
  const t = useT();
  const loadingLogoImageUrl = `${process.env.REACT_APP_ASSETS_URL}/img-logo-oval-cat.svg`;
  const navigate = useNavigate();
  const location = useLocation();
  const { user } = useAuth();
  const { showLoading, hideLoading } = useLoading();
  const [questions, setQuestions] = useState([]);
  const [likedQnaIds, setLikedQnaIds] = useState(new Set());
  const pageRef = useRef(0);
  const [hasMore, setHasMore] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [hasError, setHasError] = useState(false);

  const userId = user?.id ?? null;
  const userRole = user?.role ?? null;
  const isAdmin = userRole === 'A';

  const [isCategorySwitching, setIsCategorySwitching] = useState(false);
  const isInitialRender = useRef(true);
  const isFetching = useRef(false);

  const skeletonItems = Array.from({ length: 6 });

  const handleToggleLike = (qnaId, liked) => {
    setLikedQnaIds((prev) => {
      const next = new Set(prev);
      liked ? next.add(qnaId) : next.delete(qnaId);
      return next;
    });
  };

  const fetchQuestions = useCallback(
    async (isNextPage = false) => {
      if (activeCategory === null && useIn !== 'QnaSearch') return;
      if (isFetching.current) return;

      try {
        isFetching.current = true;
        setLoadingMore(true);
        if (!isNextPage) showLoading();

        const targetPage = isNextPage ? pageRef.current + 1 : 0;

        const data = await getQuestionList({
          categoryId: activeCategory ?? undefined,
          sort: filterState.sortBy,
          onlyAnswered: filterState.onlyAnswered,
          onlyUnanswered: filterState.onlyUnanswered,
          page: targetPage,
          isAdmin,
        });

        await new Promise((resolve) => setTimeout(resolve, 900));

        if (isNextPage) {
          setQuestions((prev) => {
            const existingIds = new Set(prev.map((q) => q.id));
            const filteredData = data.filter((q) => !existingIds.has(q.id));
            return [...prev, ...filteredData];
          });
          pageRef.current = targetPage;
        } else {
          setQuestions(data);
          pageRef.current = 0;
        }
        setHasMore(data.length === 12);
      } catch (error) {
        console.error(error);
        setHasError(true);
      } finally {
        setLoadingMore(false);
        setIsCategorySwitching(false);
        isFetching.current = false;
        if (!isNextPage) hideLoading();
      }
    },
    [
      activeCategory,
      useIn,
      filterState.sortBy,
      filterState.onlyAnswered,
      filterState.onlyUnanswered,
      isAdmin,
    ],
  );

  useEffect(() => {
    const fetchLikedQnaIds = async () => {
      try {
        const likedIds = await getUserLikeList();
        setLikedQnaIds(new Set(likedIds));
      } catch (error) {
        console.error('Failed to fetch liked QnA IDs', error);
      }
    };

    fetchLikedQnaIds();
  }, []);

  useEffect(() => {
    if (activeCategory === null && useIn !== 'QnaSearch') {
      return;
    }

    if (useIn === 'QnaSearch') {
      setIsCategorySwitching(true);

      const timer = setTimeout(() => {
        setQuestions(searchResult?.content || []);
        setHasMore(false);
        setIsCategorySwitching(false);
      }, 500);

      return () => clearTimeout(timer);
    }

    if (!isInitialRender.current && !isCategoryChangeRef.current) {
      setIsCategorySwitching(true);
    }
    setQuestions([]);
    fetchQuestions(false);
    isInitialRender.current = false;
    isCategoryChangeRef.current = false;
  }, [
    activeCategory,
    filterState.sortBy,
    fetchQuestions,
    useIn,
    searchResult,
    isCategoryChangeRef,
    filterState.onlyAnswered,
    filterState.onlyUnanswered,
  ]);

  useEffect(() => {
    if (location.state?.refresh) {
      pageRef.current = 0;
      setQuestions([]);
      setHasMore(true);

      fetchQuestions(false);

      navigate(location.pathname, { replace: true, state: {} });
    }
  }, [fetchQuestions, location.pathname, location.state, navigate]);

  const handleScroll = useCallback(() => {
    if (isFetching.current || !hasMore || hasError || pageRef.current >= 3)
      return;

    const scrollHeight = document.documentElement.scrollHeight;
    const scrollTop = window.innerHeight + window.scrollY;

    if (scrollTop >= scrollHeight - 250) {
      fetchQuestions(true);
    }
  }, [fetchQuestions, isFetching, hasMore, hasError]);

  useEffect(() => {
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, [handleScroll]);

  const handleUpdateAnswer = (questionId, updatedAnswer) => {
    setQuestions((prev) =>
      prev.map((q) =>
        q.id === questionId ? { ...q, qnaAnswer: updatedAnswer } : q,
      ),
    );
  };

  const breakpointColumnsObj = {
    default: 3,
    1800: 2,
    1350: 1,
  };
  const columnCount = breakpointColumnsObj.default;
  const emptyCount =
    questions.length < columnCount ? columnCount - questions.length : 0;

  return (
    <div className='w-full min-w-0 min-h-screen'>
      {(loadingMore && questions.length === 0) || isCategorySwitching ? (
        <Masonry
          breakpointCols={breakpointColumnsObj}
          className='flex w-full gap-6'
          columnClassName='flex flex-col gap-6'
        >
          {skeletonItems.map((_, index) => (
            <SkeletonCard
              key={`skeleton-init-${index}`}
              className='w-[352px] tabletH:w-[422px]'
            />
          ))}
        </Masonry>
      ) : (
        <Masonry
          breakpointCols={breakpointColumnsObj}
          className='flex w-full gap-6'
          columnClassName='flex flex-col gap-6'
        >
          {questions.map((q) => (
            <QuestionCard
              key={`qna-${q.id}`}
              authorProfileSrc={q.author.profileUrl}
              answererProfileSrc={
                q.qnaAnswer?.author?.profileUrl
                  ? q.qnaAnswer.author.profileUrl
                  : null
              }
              onClick={() => navigate(`/community/qna/${q.id}`)}
              data={{
                ...q,
                showCategory:
                  useIn === 'QnaSearch' ? true : activeCategory === 1,
              }}
              userId={userId}
              isHelpfulClicked={likedQnaIds.has(q.id)}
              onUpdate={handleUpdateAnswer}
              onToggleLike={handleToggleLike}
              className='w-[352px] tabletH:w-[422px]'
              searchKeyword={searchKeyword}
              useIn={useIn}
            />
          ))}
          {Array.from({ length: emptyCount }).map((_, i) => (
            <div
              key={`empty-${i}`}
              className='w-[352px] tabletH:w-[422px] invisible'
            />
          ))}
        </Masonry>
      )}

      {/* 로딩 & More questions 버튼 섹션 */}
      <div className='mt-[50px]'>
        {hasError ? (
          <div className='text-center'>
            <div className='typo-b14Strong text-text-secondary'>
              <p>{t('Oops, that didn’t work', '문제가 발생했어요')}</p>
              <p>{t('Let’s try again', '다시 시도해 주세요')}</p>
            </div>
            <BaseButton
              type='text'
              size='xl'
              icon={<ResetIcon />}
              onClick={() => {
                pageRef.current = 0;
                setQuestions([]);
                setHasMore(true);
                setHasError(false);
                fetchQuestions(false);
              }}
              bgclass='text-icon-secondary'
            />
          </div>
        ) : loadingMore ? (
          <div className='text-center typo-b16Strong text-grayscale-gray07'>
            {t('More on the way...', '더 불러오는 중...')}
          </div>
        ) : (
          hasMore &&
          pageRef.current >= 3 && (
            <div className='flex justify-center'>
              <BaseButton
                type='text'
                size='xl'
                iconPosition='after'
                icon={<ArrowIcon />}
                bgclass='rounded-[50px] text-grayscale-gray07'
                hoverclass='hover:bg-grayscale-gray02 hover:text-grayscale-gray09'
                onClick={() => fetchQuestions(true)}
              >
                {t('More questions', '질문 더 보기')}
              </BaseButton>
            </div>
          )
        )}
      </div>
    </div>
  );
};

export default QnaList;
